import { filter, isEmpty } from 'lodash';
import { useMemo } from 'react';

import { P2P_ORDER_DEEPLINK_ID } from 'constants/deeplink.constants';
import { Bank, DeviceOS, PaymentType } from 'enums';
import { Deeplink, Order, TradeMethod } from 'types';
import { deeplinkUtils, userDeviceUtils } from 'utils';

type UseDeeplinksInput = {
  order: Order | undefined;
  selectedTradeMethod: TradeMethod | undefined;
  isDeeplink: boolean;
  customerBankCode: string;
};

type UseDeeplinksResult = {
  deeplinks: Deeplink[];
  desktopQrDeeplinks: Deeplink[];
};

const VIETNAM_SBER_QR_DEEPLINK: Deeplink = {
  id: 'vietnam-sber-qr-fallback',
  customerBank: Bank.Sberbank,
  deviceOs: DeviceOS.Android,
  url: 'intent://ru.sberbankmobile/qrpay?qrPayLinkKey={{payment_string_vietnam_sber_qr}},android-app://ru.sberbankmobile/qrpay?qrPayLinkKey={{payment_string_vietnam_sber_qr}}',
};

// Android carries the ExpayOne QR payload into Sber's qrpay screen; iOS has no payload-carrying
// scheme, so it can only open the scanner and the customer scans the QR the form still renders
const EXPAYONE_SBER_QR_DEEPLINKS: Deeplink[] = [
  {
    id: 'expayone-sber-qr-fallback-android',
    customerBank: Bank.Sberbank,
    deviceOs: DeviceOS.Android,
    url: 'intent://ru.sberbankmobile/qrpay?qrPayLinkKey={{triple_urlencoded_payment_link}},android-app://ru.sberbankmobile/qrpay?qrPayLinkKey={{triple_urlencoded_payment_link}}',
  },
  {
    id: 'expayone-sber-qr-fallback-ios',
    customerBank: Bank.Sberbank,
    deviceOs: DeviceOS.IOS,
    url: 'onlineios-app://sbolonline/payments/open-barcodescanner,ios-app-smartonline://sbolonline/payments/open-barcodescanner,sbolonline://sbolonline/payments/open-barcodescanner,app-online-ios://sbolonline/payments/open-barcodescanner,onlineappmobile://sbolonline/payments/open-barcodescanner,sbolidexternallogin://sbolonline/payments/open-barcodescanner,budgetonline-ios://sbolonline/payments/open-barcodescanner,btripsexpenses://sbolonline/payments/open-barcodescanner,bank111000000111://sbolonline/payments/open-barcodescanner',
  },
];

export const useDeeplinks = ({
  order,
  selectedTradeMethod,
  isDeeplink,
  customerBankCode,
}: UseDeeplinksInput): UseDeeplinksResult => {
  const deviceOS = useMemo(() => userDeviceUtils.getDeviceOS(), []);

  const p2pProviderRequisitesDeeplinks = useMemo<Deeplink[]>(
    () =>
      isDeeplink && order?.requisites?.paymentLink
        ? [
            {
              id: P2P_ORDER_DEEPLINK_ID,
              url: order.requisites.paymentLink,
              customerBank: customerBankCode,
            },
          ]
        : [],
    [isDeeplink, order?.requisites?.paymentLink, customerBankCode],
  );

  const tradeMethodDeeplinks = useMemo<Deeplink[]>(
    () =>
      deeplinkUtils.getMatchingDeeplinks({
        deeplinks: selectedTradeMethod?.deeplinks,
        customerBank: order?.payment?.customerBank ?? undefined,
        deviceOS,
        requisites: order?.requisites,
      }),
    [
      selectedTradeMethod?.deeplinks,
      order?.payment?.customerBank,
      order?.requisites,
      deviceOS,
    ],
  );

  const desktopQrTradeMethodDeeplinks = useMemo<Deeplink[]>(
    () =>
      deeplinkUtils.getMatchingDeeplinks({
        deeplinks: selectedTradeMethod?.deeplinks,
        customerBank: order?.payment?.customerBank ?? undefined,
        deviceOS,
        requisites: order?.requisites,
        includeAllOsOnDesktop: true,
      }),
    [
      selectedTradeMethod?.deeplinks,
      order?.payment?.customerBank,
      order?.requisites,
      deviceOS,
    ],
  );

  const vietnamSberQrFallbackDeeplinks = useMemo<Deeplink[]>(
    () =>
      order?.payment?.type === PaymentType.NSPK &&
      !!order?.requisites?.paymentStringVietnamSberQR &&
      isEmpty(tradeMethodDeeplinks) &&
      VIETNAM_SBER_QR_DEEPLINK.deviceOs === deviceOS
        ? [VIETNAM_SBER_QR_DEEPLINK]
        : [],
    [
      deviceOS,
      order?.payment?.type,
      order?.requisites?.paymentStringVietnamSberQR,
      tradeMethodDeeplinks,
    ],
  );

  // Scoped to orders that actually carry an ExpayOne QR payload, so plain NSPK orders keep
  // falling through to their own configured deeplinks
  const expayOneSberQrFallbackDeeplinks = useMemo<Deeplink[]>(
    () =>
      order?.payment?.type === PaymentType.NSPK &&
      !!order?.requisites?.paymentStringExpayOneSberQR &&
      isEmpty(tradeMethodDeeplinks)
        ? filter(
            EXPAYONE_SBER_QR_DEEPLINKS,
            ({ deviceOs }) => deviceOs === deviceOS,
          )
        : [],
    [
      deviceOS,
      order?.payment?.type,
      order?.requisites?.paymentStringExpayOneSberQR,
      tradeMethodDeeplinks,
    ],
  );

  const fallbackDeeplinks = useMemo<Deeplink[]>(
    () =>
      isEmpty(vietnamSberQrFallbackDeeplinks)
        ? expayOneSberQrFallbackDeeplinks
        : vietnamSberQrFallbackDeeplinks,
    [expayOneSberQrFallbackDeeplinks, vietnamSberQrFallbackDeeplinks],
  );

  if (!isEmpty(p2pProviderRequisitesDeeplinks)) {
    return {
      deeplinks: p2pProviderRequisitesDeeplinks,
      desktopQrDeeplinks: p2pProviderRequisitesDeeplinks,
    };
  }

  return {
    deeplinks: isEmpty(tradeMethodDeeplinks)
      ? fallbackDeeplinks
      : tradeMethodDeeplinks,
    desktopQrDeeplinks: isEmpty(desktopQrTradeMethodDeeplinks)
      ? fallbackDeeplinks
      : desktopQrTradeMethodDeeplinks,
  };
};
